import { CreateComponentSettings } from '../model';
import {
  BaseStorage,
  BaseStorageOptions,
  DataCapsule,
} from '@wix/fe-essentials/data-capsule';
import {
  AccountPreferencesStrategy,
  WixStorageStrategyOptions,
} from './AccountPreferencesStrategy';
import { AccountPreferencesMergeStrategy } from './AccountPreferencesMergeStrategy';

function isDataCapsuleStorage(storageStrategy: {}): storageStrategy is {
  storageStrategy: BaseStorage<WixStorageStrategyOptions>;
} {
  return 'storageStrategy' in storageStrategy;
}

export const createAccountComponentSettings: CreateComponentSettings = ({
  httpClient,
  environment,
  createEssentials,
}) => {
  return (options) => {
    const scope = environment.componentId || environment.artifactId;

    const { dataCapsule: _dataCapsule } = createEssentials({
      dataCapsule: {
        storageStrategy: 'userPreferences',
        namespace: [scope, options.namespace].join('.'),
      },
    });

    const {
      dataCapsule: oldLocalStorageComponentSettingsDataCapsule,
      fedopsLogger,
    } = createEssentials({
      dataCapsule: {
        namespace: options.namespace,
        scope,
      } as { namespace: string; scope: string },
      fedops: {
        appName: 'cairo',
      },
    });

    const dataCapsule = _dataCapsule as unknown as Omit<
      DataCapsule,
      'storageStrategy'
    > & {
      storageStrategy: BaseStorage<BaseStorageOptions>;
    };

    if (isDataCapsuleStorage(dataCapsule.storageStrategy)) {
      dataCapsule.storageStrategy = new AccountPreferencesMergeStrategy({
        storage: new AccountPreferencesStrategy({
          httpClient,
        }),
      });
    }

    (_dataCapsule as { migrateItem?: (key: string) => unknown }).migrateItem =
      async (key: string) => {
        const localData = await oldLocalStorageComponentSettingsDataCapsule
          .getItem(key)
          .catch(() => null);

        if (!localData) {
          return;
        }

        fedopsLogger?.interactionStarted(
          'account-component-settings-local-storage-migration',
        );

        const data = await dataCapsule.getItem(key).catch(() => null);

        await dataCapsule.setItem(key, {
          views: {
            list: {
              ...localData?.views?.list,
              ...data?.views?.list,
            },
          },
        });

        await oldLocalStorageComponentSettingsDataCapsule.removeItem(key);

        fedopsLogger?.interactionEnded(
          'account-component-settings-local-storage-migration',
        );
      };

    return _dataCapsule;
  };
};
